import { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAccount } from "~/hooks/useAccount";
import { skipTrialDiscount } from "~/api/billing/requests";
import { toast } from "~/components/toast";
import { PlanStatus } from "~/types/account";
import * as S from "./styles";

const CLOSE_DELAY_MS = 60_000;
const DISMISSED_KEY = "skip_trial_banner_dismissed";

export const SkipTrialBanner = () => {
  const { t } = useTranslation();
  const { account, refetch } = useAccount();
  const navigate = useNavigate();
  const [canClose, setCanClose] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === "true"
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const mountTimeRef = useRef(Date.now());

  useEffect(() => {
    const elapsed = Date.now() - mountTimeRef.current;
    const remaining = Math.max(CLOSE_DELAY_MS - elapsed, 0);
    const timer = setTimeout(() => setCanClose(true), remaining);
    return () => clearTimeout(timer);
  }, []);

  const isTrialing = useCallback(() => {
    if (!account?.active_subscription) return false;
    if (account.active_subscription.status !== PlanStatus.ACTIVE) return false;
    if (!account.active_subscription.trial_end_at) return false;
    return new Date(account.active_subscription.trial_end_at) > new Date();
  }, [account]);

  const shouldShow =
    !dismissed &&
    account &&
    !account.has_redeemed_skip_trial_coupon &&
    (account.payment_provider === "stripe" ||
      account.active_subscription?.payment_provider === "stripe") &&
    isTrialing();

  const handleClose = () => {
    if (!canClose) return;
    setDismissed(true);
    sessionStorage.setItem(DISMISSED_KEY, "true");
  };

  const handleConfirm = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await skipTrialDiscount();
      setShowConfirm(false);
      await refetch();
      toast.success(t("skip_trial.success_description") as string, {
        title: t("skip_trial.success_title") as string,
      });
      setDismissed(true);
      sessionStorage.setItem(DISMISSED_KEY, "true");
    } catch (err: any) {
      const message =
        err?.response?.data?.error || "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!shouldShow) return null;

  const cardDigits = account?.stripe_card_digits;

  return (
    <>
      {/* Sticky Banner - bottom left dark card */}
      {!showConfirm && (
        <S.BannerContainer>
          {canClose && (
            <S.BannerCloseButton
              onClick={handleClose}
              aria-label="Close"
              title="Close">
              &#x2715;
            </S.BannerCloseButton>
          )}
          <S.BannerTitle>{t("skip_trial.banner_title")}</S.BannerTitle>
          <S.BannerDescription>
            {t("skip_trial.banner_description")}
          </S.BannerDescription>
          <S.ClaimButton onClick={() => setShowConfirm(true)}>
            {t("skip_trial.claim_discount")}
          </S.ClaimButton>
        </S.BannerContainer>
      )}

      {/* Confirmation Modal */}
      {showConfirm &&
        ReactDOM.createPortal(
          <S.ModalOverlay onClick={() => !isLoading && setShowConfirm(false)}>
            <S.ConfirmModal onClick={(e) => e.stopPropagation()}>
              <S.ConfirmCloseButton
                onClick={() => !isLoading && setShowConfirm(false)}>
                &#x2715;
              </S.ConfirmCloseButton>

              <S.ConfirmTitle>{t("skip_trial.confirm_title")}</S.ConfirmTitle>
              <S.ConfirmDescription>
                {t("skip_trial.confirm_description")}
              </S.ConfirmDescription>

              <S.TestimonialRow>
                <S.TestimonialAvatar>
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      background: "#c4a882",
                      borderRadius: "50%",
                    }}
                  />
                </S.TestimonialAvatar>
                <S.TestimonialText>
                  {t("skip_trial.confirm_testimonial")}
                </S.TestimonialText>
              </S.TestimonialRow>

              {cardDigits && (
                <S.CardSection>
                  <S.CardInfo>
                    <S.CardLabel>
                      {t("skip_trial.credit_debit_card")}
                    </S.CardLabel>
                    <S.CardNumber>**** **** **** {cardDigits}</S.CardNumber>
                  </S.CardInfo>
                  <S.ChangeCardLink
                    onClick={() => {
                      setShowConfirm(false);
                      navigate("/settings/account");
                    }}>
                    {t("skip_trial.change_card")}
                  </S.ChangeCardLink>
                </S.CardSection>
              )}

              <S.GetDiscountButton
                onClick={handleConfirm}
                disabled={isLoading}>
                {isLoading ? "Processing..." : t("skip_trial.confirm_button")}
              </S.GetDiscountButton>
            </S.ConfirmModal>
          </S.ModalOverlay>,
          document.body
        )}
    </>
  );
};
